import cx from 'classnames';
import { t } from 'i18next';
import _ from 'lodash';
import Numeral from 'numeral';
import { Component, ReactElement, useState } from 'react';
import { sortByShortName } from '../../../../../../shared/frontend/libs/utils';
import { filterDungeonsBySeason } from '../../../../shared/zones';
import { getSeasonBySlug } from '../../../../shared/enums';
import { slugify } from '../../../../../../shared/common/slugify';
import { namedRoutes, resolveRoutePath } from '../../bundles/routes';
import { GuildRank } from '../../components/presentational/guildRank';
import { Link } from '../../../../../../shared/frontend/components/link';
import { Media } from '../../../../../../shared/frontend/components/presentational/media';
import { PossibleStreamTooltip } from '../../../../../../shared/frontend/components/presentational/streamPreview';
import { SimpleTooltip } from '../../../../../../shared/frontend/components/presentational/tooltip';
import { TRegionSummary } from '../../libs/store';
import { RecruitmentProfileBadge } from '../../shared/recruitment/components/RecruitmentProfileBadge';
import { affixIncreasedTimeMs } from '../../../../../../shared/common/affixHelpers/affixIncreasedTimeMs';
import { MythicDungeonLink, MythicDungeonRunIcon } from '../../mythicRuns/components/MythicDungeonRunIcon';
import { TScoringSystem } from '../../../../../../shared/gameConfig/mythicPlus';
import { RunTimeTooltipContent } from '../../../../../../shared/frontend/components/presentational/runTimeTooltip';

export type TTeamRankingsTableProps = {
  region: TRegionSummary;
  teamDetailsRoute: any;
  makeRankingsRoute: any;
  teams: any[];
  title?: string;
  subTitle?: any;
  titleSpecifiers?: string[];
  dungeons: any[];
  season: string;
  config: any;
  getTeamLink: Function;
  footer?: any;
  compact?: boolean;
};

/**
 * TeamRankingsTable
 */
export class TeamRankingsTable extends Component<TTeamRankingsTableProps> {
  public render(): ReactElement<TTeamRankingsTableProps> {
    return (
      <>
        <Media at="sm">
          <TeamRankingsTableSmall {...this.props} />
        </Media>
        <Media greaterThan="sm">
          <TeamRankingsTableLarge {...this.props} />
        </Media>
      </>
    );
  }
}

/**
 * EmptySlate
 */
function EmptySlate(props: any) {
  return <span>{t('common:errors.empty-team-leaderboard')}</span>;
}

/**
 * TeamRankingsTableSmall
 */
export function TeamRankingsTableSmall(props: TTeamRankingsTableProps) {
  const defaultColumns = {
    rank: true,
    region: false,
    tank: true,
    healer: true,
    dps: true,
    runs: false,
    faction: false,
    teamName: false,
    totalTime: false
  };

  const maxVisibleRuns = props.config && props.config.maxVisibleRuns ? props.config.maxVisibleRuns : 5;

  const cols =
    props.config && props.config.columns
      ? {
          ...defaultColumns,
          ...props.config.columns
        }
      : defaultColumns;

  let title: any = props.title;
  if (!title) {
    const titlePrefix = props.titleSpecifiers ? props.titleSpecifiers.join(' ') : '';
    if (titlePrefix.length > 15) {
      title = (
        <span>
          {titlePrefix}
          <br />
          {props.subTitle}
        </span>
      );
    } else {
      title = (
        <span>
          {titlePrefix} {props.subTitle}
        </span>
      );
    }
  }

  return (
    <table
      className={cx(
        'slds-table',
        'slds-table--bordered',
        'slds-table--striped',
        'slds-table--cell-buffer',
        'rio-rankings-table'
      )}
    >
      <thead>
        <tr>
          <th colSpan={4} className="slds-text-align--center text-white slds-text-heading--small">
            {title}
          </th>
        </tr>
      </thead>
      <tbody>
        {props.teams.length === 0 && (
          <tr>
            <td colSpan={4}>
              <div
                className="rio-rankings-table slds-table slds-p-around--large slds-align--absolute-center"
                style={{ minHeight: '250px', whiteSpace: 'normal' }}
              >
                <div className="slds-text-align--center">
                  <p className="slds-text-heading--medium">
                    <EmptySlate region={props.region.slug} />
                  </p>
                </div>
              </div>
            </td>
          </tr>
        )}

        {props.teams.map((entry: any) => (
          <tr key={entry.rank} className={`faction--${entry.faction} mythic-plus-rankings--row`}>
            {cols.rank && (
              <td data-label="Rank" className="slds-text-align--center">
                <GuildRank rank={entry.rank} />
              </td>
            )}
            {cols.teamName && (
              <td className="slds-text-align--left text-white">
                <Link
                  className={`rio-realm-link rio-faction-color--${entry.team.faction} ${
                    entry.streamers.count > 0 ? 'streaming-now' : ''
                  }`}
                  to={props.getTeamLink(props.teamDetailsRoute, entry)}
                >
                  <PossibleStreamTooltip stream={entry.streamers.stream} count={entry.streamers.count}>
                    {entry.team.name}
                  </PossibleStreamTooltip>
                </Link>

                {entry.recruitmentProfiles?.length > 0 && (
                  <RecruitmentProfileBadge
                    link={resolveRoutePath(namedRoutes.TeamRecruitment, {
                      region: entry.team.region.slug,
                      team: slugify(entry.team.name)
                    })}
                    className="slds-m-left--xx-small"
                    profile={entry.recruitmentProfiles[0].recruitment_profile_id}
                  />
                )}
              </td>
            )}
            {cols.runs && (
              <td>
                <span className="rio-rankings-bossrow">
                  {entry.runs
                    .sort((a: any, b: any) => b.score - a.score)
                    .slice(0, maxVisibleRuns)
                    .map((run: any) => {
                      const dungeon = props.dungeons.find((dgn: any) => dgn.id === run.zoneId);
                      if (!dungeon) {
                        return null;
                      }

                      const challengersPerilOffset = affixIncreasedTimeMs(run.affixes);
                      const keystoneTimeMs = dungeon.keystone_timer_ms + challengersPerilOffset;
                      return (
                        <SimpleTooltip
                          key={dungeon.id}
                          placement="right"
                          className="rio-spaced-element--medium slds-text-align--center d-inline-block"
                          tooltip={
                            <RunTimeTooltipContent
                              className="slds-text-body--small"
                              run={{
                                clearTimeMs: run.clearTimeMs,
                                keystoneTimeMs
                              }}
                              useShortFormat={false}
                              prefix={
                                <p>
                                  +{run.mythicLevel} {dungeon.name}
                                </p>
                              }
                              suffix={run.score ? <p>Score {Numeral(run.score).format('0,0.0')}</p> : undefined}
                            />
                          }
                        >
                          <MythicDungeonLink season={props.season} dungeon={dungeon} runs={entry.runs} />
                        </SimpleTooltip>
                      );
                    })}
                </span>
              </td>
            )}
            {cols.score && (
              <td className="slds-text-align--right">
                <Link
                  className="rio-realm-link slds-text-body--large"
                  style={{ color: entry.scoreColor }}
                  to={props.getTeamLink(props.teamDetailsRoute, entry)}
                >
                  <b>{Numeral(entry.score).format('0.0')}</b>
                </Link>
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function TeamRankingsTableLarge(props: TTeamRankingsTableProps) {
  const defaultColumns = {
    rank: true,
    region: true,
    tank: true,
    healer: true,
    dps: true,
    runs: true,
    faction: true,
    teamName: false,
    totalTime: false,
    score: false
  };

  const cols =
    props.config && props.config.columns
      ? {
          ...defaultColumns,
          ...props.config.columns
        }
      : defaultColumns;

  const seasonInfo = getSeasonBySlug(props.season);

  const numVisibleCols = _.values(cols).filter((v: any) => v === true).length;

  const filteredDungeons = props.dungeons.filter(filterDungeonsBySeason(seasonInfo)).sort(sortByShortName);

  const [showAlternate, setShowAlternate] = useState(false);

  const numColumns =
    Number(cols.rank || 0) +
    Number(cols.region && props.region.slug === 'world') +
    Number(cols.teamName || 0) +
    Number(cols.runs || 0) +
    Number(cols.score || 0);

  return (
    <table
      className={cx(
        'slds-table slds-table--bordered slds-table--striped rio-rankings-table',
        props.compact ? '' : 'slds-table--cell-buffer'
      )}
    >
      <thead>
        {props.title ? (
          <tr>
            <th colSpan={numColumns}>{props.title}</th>
          </tr>
        ) : (
          <tr>
            {cols.rank && (
              <th style={{ width: '6rem' }} className="slds-text-align--center text-white slds-text-heading--small">
                Rank
              </th>
            )}
            {cols.region && props.region.slug === 'world' && (
              <th style={{ width: '6rem' }} className="slds-text-align--center text-white slds-text-heading--small">
                Region
              </th>
            )}
            {cols.teamName && (
              <th
                style={{ width: '14rem' }}
                className="slds-text-align--left text-white slds-text-heading--small slds-cell-wrap"
              >
                Team
              </th>
            )}
            {cols.runs && (
              <th className="slds-text-align--left text-white slds-text-heading--small">
                Best Runs
                {seasonInfo.scoringSystem >= TScoringSystem.V4 && (
                  <span
                    className="text-raiderio slds-m-left--x-small slds-text-body--small"
                    onClick={() => setShowAlternate(!showAlternate)}
                    style={{ cursor: 'pointer' }}
                  >
                    {showAlternate && '[Hide Alternates]'}
                    {!showAlternate && '[Show Alternates]'}
                  </span>
                )}
              </th>
            )}
            {cols.score && (
              <th style={{ width: '4rem' }} className="slds-text-align--right slds-text-heading--small">
                Score
              </th>
            )}
          </tr>
        )}
      </thead>
      <tbody>
        {props.teams.length === 0 && (
          <tr>
            <td colSpan={numVisibleCols}>
              <div className="slds-align--absolute-center" style={{ minHeight: '250px', whiteSpace: 'normal' }}>
                <div className="slds-text-align--center">
                  <p className="slds-text-heading--medium">
                    <EmptySlate region={props.region.slug} />
                  </p>
                </div>
              </div>
            </td>
          </tr>
        )}

        {props.teams.map((entry: any) => (
          <tr key={entry.rank} className={`faction--${entry.team.faction} mythic-plus-rankings--row`}>
            {cols.rank && (
              <td data-label="Rank" className="slds-text-align--center">
                <GuildRank rank={entry.rank} />
              </td>
            )}
            {cols.region && props.region.slug === 'world' && (
              <td className="slds-text-align--center slds-text-body--medium">
                <Link className="rio-realm-link" to={props.makeRankingsRoute({ region: entry.team.region.slug })}>
                  {entry.team.region.short_name}
                </Link>
                {entry.team.subRegion && (
                  <>
                    {' '}
                    <Link
                      className="rio-realm-link"
                      to={props.makeRankingsRoute({ region: entry.team.subRegion.slug })}
                    >
                      ({entry.team.subRegion.short_name})
                    </Link>
                  </>
                )}
              </td>
            )}
            {cols.teamName && (
              <td className="slds-text-align--left text-white slds-cell-wrap">
                <Link
                  className={`rio-realm-link rio-faction-color--${entry.team.faction} ${
                    entry.streamers.count > 0 ? 'streaming-now' : ''
                  }`}
                  to={props.getTeamLink(props.teamDetailsRoute, entry)}
                >
                  <PossibleStreamTooltip stream={entry.streamers.stream} count={entry.streamers.count}>
                    {entry.team.name}
                  </PossibleStreamTooltip>
                </Link>

                {entry.recruitmentProfiles?.length > 0 && (
                  <RecruitmentProfileBadge
                    link={resolveRoutePath(namedRoutes.TeamRecruitment, {
                      region: entry.team.region.slug,
                      team: slugify(entry.team.name)
                    })}
                    className="slds-m-left--xx-small"
                    profile={entry.recruitmentProfiles[0].recruitment_profile_id}
                  />
                )}
              </td>
            )}
            {cols.runs && (
              <td>
                <div className="rio-rankings-bossrow">
                  {filteredDungeons.map((dungeon: any) => {
                    return (
                      <MythicDungeonRunIcon
                        key={dungeon.id}
                        dungeon={dungeon}
                        runs={entry.runs}
                        seasonInfo={seasonInfo}
                        isAlternate={false}
                      />
                    );
                  })}
                </div>
                {seasonInfo.scoringSystem >= TScoringSystem.V4 && showAlternate && (
                  <div className="rio-rankings-bossrow slds-m-top--x-small">
                    {filteredDungeons.map((dungeon: any) => {
                      return (
                        <MythicDungeonRunIcon
                          key={dungeon.id}
                          dungeon={dungeon}
                          runs={entry.alternateRuns}
                          seasonInfo={seasonInfo}
                          isAlternate={true}
                        />
                      );
                    })}
                  </div>
                )}
              </td>
            )}
            {cols.score && (
              <td className="slds-text-align--right">
                <Link
                  className="rio-realm-link slds-text-body--large"
                  style={{ color: entry.scoreColor }}
                  to={props.getTeamLink(props.teamDetailsRoute, entry)}
                >
                  <b>{Numeral(entry.score).format(props.compact ? '0' : '0.0')}</b>
                </Link>
              </td>
            )}
          </tr>
        ))}
      </tbody>
      {props.footer}
    </table>
  );
}
